import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  Calendar, Clock, MapPin, User, DollarSign, 
  Car, Package, ShoppingBag, Shirt, ChevronRight,
  RefreshCw, AlertCircle, CheckCircle
} from 'lucide-react';
import { Button } from '../ui/button';
import api from '../../services/api';

const SERVICE_ICONS = {
  rides: Car,
  courier: Package,
  errands: ShoppingBag,
  laundry: Shirt
};

const STATUS_COLORS = {
  pending: { bg: 'bg-yellow-500/20', text: 'text-yellow-400', label: 'Pending' },
  assigned: { bg: 'bg-blue-500/20', text: 'text-blue-400', label: 'Assigned' },
  in_progress: { bg: 'bg-purple-500/20', text: 'text-purple-400', label: 'In Progress' },
  completed: { bg: 'bg-green-500/20', text: 'text-green-400', label: 'Completed' },
  cancelled: { bg: 'bg-red-500/20', text: 'text-red-400', label: 'Cancelled' }
};

const TodaySchedule = ({ onViewAll }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({ total: 0, pending: 0, inProgress: 0, completed: 0, revenue: 0 });

  useEffect(() => {
    fetchTodayBookings();
  }, []);

  const fetchTodayBookings = async () => {
    setLoading(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await api.get('/bookings');
      const allBookings = Array.isArray(response.data) ? response.data : [];
      
      // Filter for today's bookings
      const todayBookings = allBookings.filter(b => b.scheduled_date === today);
      
      // Sort by time
      todayBookings.sort((a, b) => {
        const timeA = a.scheduled_time || '00:00';
        const timeB = b.scheduled_time || '00:00';
        return timeA.localeCompare(timeB);
      });
      
      setBookings(todayBookings);
      
      // Calculate stats
      const pending = todayBookings.filter(b => b.status === 'pending').length;
      const inProgress = todayBookings.filter(b => b.status === 'in_progress' || b.status === 'assigned').length;
      const completed = todayBookings.filter(b => b.status === 'completed').length;
      const revenue = todayBookings
        .filter(b => b.status === 'completed')
        .reduce((sum, b) => sum + (b.total_amount || 0), 0);
      
      setStats({
        total: todayBookings.length,
        pending,
        inProgress,
        completed,
        revenue
      });
    } catch (error) {
      console.error('Failed to fetch today bookings:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timeStr) => {
    if (!timeStr) return '';
    try {
      const [hours, minutes] = timeStr.split(':');
      const hour = parseInt(hours);
      const ampm = hour >= 12 ? 'PM' : 'AM';
      const displayHour = hour % 12 || 12;
      return `${displayHour}:${minutes} ${ampm}`;
    } catch {
      return timeStr;
    }
  };

  const ServiceIcon = ({ type }) => {
    const Icon = SERVICE_ICONS[type] || Package;
    return <Icon className="w-4 h-4" />;
  };

  if (loading) {
    return (
      <div className="card p-6">
        <div className="flex items-center justify-center py-8">
          <RefreshCw className="w-6 h-6 text-[var(--primary)] animate-spin" />
        </div>
      </div>
    );
  }

  const today = new Date();
  const dateStr = today.toLocaleDateString('en-US', { 
    weekday: 'long', 
    month: 'long', 
    day: 'numeric' 
  });

  return (
    <div className="card overflow-hidden" data-testid="today-schedule">
      {/* Header */}
      <div className="bg-gradient-to-r from-[var(--primary)]/20 to-transparent p-4 border-b border-[var(--border)]">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[var(--primary)] flex items-center justify-center">
              <Calendar className="w-5 h-5 text-black" />
            </div>
            <div>
              <h3 className="font-bold text-lg">Today's Schedule</h3>
              <p className="text-sm text-[var(--text-muted)]">{dateStr}</p>
            </div>
          </div>
          <Button variant="ghost" size="sm" onClick={fetchTodayBookings}>
            <RefreshCw className="w-4 h-4" />
          </Button>
        </div>
      </div>

      {/* Quick Stats */}
      <div className="grid grid-cols-4 gap-2 p-4 border-b border-[var(--border)]">
        <div className="text-center">
          <p className="text-2xl font-bold text-[var(--primary)]">{stats.total}</p>
          <p className="text-xs text-[var(--text-muted)]">Total</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-yellow-400">{stats.pending}</p>
          <p className="text-xs text-[var(--text-muted)]">Pending</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-purple-400">{stats.inProgress}</p>
          <p className="text-xs text-[var(--text-muted)]">Active</p>
        </div>
        <div className="text-center">
          <p className="text-2xl font-bold text-green-400">{stats.completed}</p>
          <p className="text-xs text-[var(--text-muted)]">Done</p>
        </div>
      </div>

      {/* Bookings List */}
      <div className="max-h-[400px] overflow-y-auto">
        {bookings.length === 0 ? (
          <div className="p-8 text-center">
            <Calendar className="w-12 h-12 text-[var(--text-muted)] mx-auto mb-3 opacity-50" />
            <p className="text-[var(--text-muted)] mb-1">No bookings scheduled for today</p>
            <p className="text-xs text-[var(--text-secondary)]">New bookings will appear here</p>
          </div>
        ) : (
          <div className="divide-y divide-[var(--border)]">
            {bookings.map((booking, idx) => {
              const status = STATUS_COLORS[booking.status] || STATUS_COLORS.pending;
              return (
                <motion.div
                  key={booking.id}
                  initial={{ opacity: 0, x: -20 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: idx * 0.05 }}
                  className="p-4 hover:bg-[var(--background-subtle)] cursor-pointer transition-colors"
                >
                  <div className="flex items-start gap-3">
                    {/* Time */}
                    <div className="w-16 text-center flex-shrink-0">
                      <p className="font-bold text-[var(--primary)]">{formatTime(booking.scheduled_time)}</p>
                      <p className="text-xs text-[var(--text-muted)]">{booking.duration_hours || 1}hr</p>
                    </div>
                    
                    {/* Details */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <ServiceIcon type={booking.service_type} />
                        <span className="font-medium truncate">{booking.service_name || booking.service_type}</span>
                      </div>
                      <div className="flex items-center gap-2 text-sm text-[var(--text-muted)]">
                        <User className="w-3 h-3" />
                        <span className="truncate">{booking.customer_name || booking.guest_name || 'Guest'}</span>
                      </div>
                      <div className="flex items-center gap-2 text-xs text-[var(--text-secondary)] mt-1">
                        <MapPin className="w-3 h-3" />
                        <span className="truncate">{booking.city}, {booking.state}</span>
                      </div>
                    </div>
                    
                    {/* Status & Price */}
                    <div className="text-right flex-shrink-0">
                      <span className={`text-xs px-2 py-1 rounded-full ${status.bg} ${status.text}`}>
                        {status.label}
                      </span>
                      {booking.total_amount > 0 && (
                        <p className="text-sm font-bold text-[var(--primary)] mt-1">
                          ${booking.total_amount.toFixed(0)}
                        </p>
                      )}
                      {booking.worker_name && (
                        <p className="text-xs text-[var(--text-muted)] mt-1 truncate max-w-[80px]">
                          {booking.worker_name}
                        </p>
                      )}
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>

      {/* Footer */}
      {stats.total > 0 && (
        <div className="p-4 border-t border-[var(--border)] bg-[var(--background-subtle)]">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-[var(--text-muted)]">Today's Revenue</p>
              <p className="text-xl font-bold text-green-400">${stats.revenue.toFixed(2)}</p>
            </div>
            {onViewAll && (
              <Button variant="outline" size="sm" onClick={onViewAll}>
                View All
                <ChevronRight className="w-4 h-4 ml-1" />
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default TodaySchedule;
